import { useCallback } from "react";
import { t } from "i18next";
import { round } from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { PAYMENT_OPTIONS, SOURCE } from "@constants/Bookings";
import { slotType } from "@enums/hooks.enum";
import { DateFormats } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { UseBookingSubmitParams } from "@models/components/booking.model";
import { applyLocalization } from "@services/common.service";
import { RootState } from "@store/index";
import { getPriceInfo } from "@utils/amenity.utils";

export const useBookingSubmit = ({
  amenitiesList,
  grandTotal,
  selectedAmenitie,
  selectedPaymentMethod,
  setGuestError,
  totalPriceWithTax,
}: UseBookingSubmitParams) => {
  const { bookingId } = useParams<{ bookingId: string }>();

  const { bookSlot } = useBooking();

  const {
    bookingEditData,
    checkparticipantErrors,
    guestList,
    isAmenityEdit,
    isEditBooking,
    resourcePrices,
    selectedDate,
    selectedSlot,
    selectedTime,
    selectedTokenizationId,
  } = useSelector((State: RootState) => State.booking);

  const handleBookingSubmit = useCallback(() => {
    const selectedResourceId = selectedSlot?.resourceId;
    const currentResourcePrice = selectedResourceId
      ? resourcePrices?.prices?.[selectedResourceId]
      : undefined;

    const isTokenizationAllowed =
      currentResourcePrice &&
      typeof currentResourcePrice.allowTokenization === "boolean"
        ? currentResourcePrice.allowTokenization
        : true;

    if (
      isEditBooking &&
      !isTokenizationAllowed &&
      bookingEditData?.tokenizationId !== null &&
      bookingEditData?.tokenizationId !== undefined
    ) {
      return false;
    }

    const min = selectedSlot?.slot?.min ?? 0;
    const max = selectedSlot?.slot?.max ?? Infinity;
    const participantCount = guestList.length;
    const allowBringingGuest = selectedSlot?.slot?.allowBringingGuest;
    const minguestIgnoreAmenities = selectedSlot?.slot?.minguestIgnoreAmenities;
    const hasIgnoredGuest = selectedAmenitie?.some(
      amentity =>
        amentity.count > 0 && minguestIgnoreAmenities?.includes(amentity.id)
    );
    if (Object.keys(checkparticipantErrors).length > 0) {
      setGuestError(t("addGuest.errorInParticipant"));
      return false;
    }

    if (!allowBringingGuest && participantCount > 0) {
      setGuestError(t("addGuest.guestRestrictionOnPolicy"));
      return false;
    }

    if (!hasIgnoredGuest && allowBringingGuest && participantCount < min) {
      setGuestError(t("addGuest.minGuestRequired", { min }));
      return false;
    }

    if (allowBringingGuest && participantCount > max) {
      setGuestError(t("addGuest.maxGuestsExceeded", { max }));
      return false;
    }

    setGuestError("");

    // Build final amenities payload
    let finalAmenities = [];

    if (isEditBooking && bookingEditData?.amenities) {
      const currentAmenitiesPayload: {
        amenityId: string;
        bookingAmenityId: number | null;
        isDeleted: number;
        quantity: number;
      }[] = [];

      // 1. Process selected amenities: find newly added ones or ones with changed quantity
      selectedAmenitie?.forEach(am => {
        if (am.bookingAmenityId) {
          const original = bookingEditData.amenities?.find(
            orig => orig.bookingAmenityId === am.bookingAmenityId
          );
          if (original) {
            let priceChanged = false;
            if (amenitiesList) {
              const currentAmenity = amenitiesList.find(a => a.id === am.id);
              if (currentAmenity) {
                const resourceId = selectedSlot?.resourceId;
                const duration = selectedSlot?.slot?.slot;
                if (resourceId && duration) {
                  const resourceData = currentAmenity.resources?.[resourceId];
                  const priceInfo = resourceData
                    ? getPriceInfo(resourceData.prices, duration)
                    : null;
                  const currentPrice = priceInfo?.totalPrice ?? 0;
                  const originalUnitPrice =
                    original.quantity > 0 ? original.unitPrice : 0;
                  if (originalUnitPrice !== currentPrice) {
                    priceChanged = true;
                  }
                }
              }
            }
            // Only include if quantity changed or price changed
            if (am.count !== original.quantity || priceChanged) {
              currentAmenitiesPayload.push({
                amenityId: am.id,
                bookingAmenityId: am.bookingAmenityId as number,
                isDeleted: 0,
                quantity: am.count,
              });
            }
          } else {
            // Not found in original, send as new
            currentAmenitiesPayload.push({
              amenityId: am.id,
              bookingAmenityId: null,
              isDeleted: 0,
              quantity: am.count,
            });
          }
        } else {
          // Newly added amenity
          currentAmenitiesPayload.push({
            amenityId: am.id,
            bookingAmenityId: null,
            isDeleted: 0,
            quantity: am.count,
          });
        }
      });

      // 2. Identify deleted amenities (were in original booked amenities but not in selected amenities anymore)
      const deletedAmenitiesPayload = bookingEditData.amenities
        .filter(orig => {
          if (orig.bookingAmenityId) {
            return !selectedAmenitie?.some(
              sa => sa.bookingAmenityId === orig.bookingAmenityId
            );
          }
          return !selectedAmenitie?.some(sa => sa.id === orig.id);
        })
        .map(orig => ({
          amenityId: orig.id,
          bookingAmenityId: (orig.bookingAmenityId as number) || null,
          isDeleted: 1,
          quantity: orig.quantity,
        }));

      finalAmenities = [...currentAmenitiesPayload, ...deletedAmenitiesPayload];
    } else {
      // Create mode: send all selected amenities
      finalAmenities =
        selectedAmenitie?.map(am => ({
          amenityId: am.id,
          bookingAmenityId: null,
          isDeleted: 0,
          quantity: am.count,
        })) || [];
    }

    const extrasList = selectedAmenitie
      ?.filter(sa => sa && sa.count > 0 && !sa.isDeleted)
      ?.map(sa => {
        const item = amenitiesList?.find(a => a.id === sa.id);
        let title = item?.title ? applyLocalization(item.title) : "";
        if (!title && isEditBooking && bookingEditData?.amenities) {
          const editItem = bookingEditData.amenities.find(a => a.id === sa.id);
          if (editItem?.title) {
            title = applyLocalization(editItem.title);
          }
        }
        return title ? `${title} × ${sa.count}` : "";
      })
      ?.filter(Boolean);

    const extras =
      extrasList && extrasList.length > 0 ? extrasList.join("\n") : undefined;

    // Build common payload
    const tokenizationId =
      selectedPaymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT
        ? selectedTokenizationId && selectedTokenizationId !== "new"
          ? selectedTokenizationId
          : null
        : undefined;

    const commonPayload = {
      amenities: finalAmenities,
      date: selectedDate,
      extras,
      participants: guestList.map(p => p.profileUuid),
      paymentMethod: selectedPaymentMethod,
      resourceId: selectedSlot?.resourceId,
      slotType:
        selectedSlot?.slot?.type === slotType.TimeSlots
          ? selectedSlot?.slot?.type
          : selectedSlot?.slot?.slot,
      source: globalThis.matchMedia("(display-mode: standalone)").matches
        ? SOURCE.PWA
        : SOURCE.WEB,
      timeSlot: selectedSlot?.slot?.slot,
      tokenizationId,
      totalPrice: round(grandTotal ?? totalPriceWithTax, 2),
    };

    const basePayload =
      selectedSlot?.slot?.type === slotType.TimeSlots
        ? {
            ...commonPayload,
            endTime: moment(selectedTime, DateFormats.Time)
              .add(selectedSlot?.slot?.slot, DateFormats.Minutes)
              .format(DateFormats.Time),
            startTime: selectedTime,
          }
        : {
            ...commonPayload,
            endTime: selectedSlot?.slot?.ends,
            startTime: selectedSlot?.slot?.starts,
          };

    const finalPayload = isEditBooking
      ? {
          ...basePayload,
          bookingId,
          isAmenityEdit,
          version: bookingEditData?.version ?? 1,
        }
      : basePayload;

    bookSlot(finalPayload);

    return true;
  }, [
    selectedSlot,
    guestList,
    checkparticipantErrors,
    selectedDate,
    selectedTime,
    selectedPaymentMethod,
    selectedTokenizationId,
    totalPriceWithTax,
    grandTotal,
    setGuestError,
    bookSlot,
    selectedAmenitie,
    isAmenityEdit,
    amenitiesList,
  ]);

  return { handleBookingSubmit };
};
